/**
 * The whole text of anything the screen cut short with an ellipsis, shown
 * when the pointer rests on the "…" itself.
 *
 * Field labels are the usual case: an outlined label that does not fit its
 * field ends in "…" ("Κέντρο: …", "Στάθμη ε…") and there was no way to read
 * the rest short of widening the window. One listener for the whole app
 * rather than a tooltip per field: it covers every label, tab, chip and
 * table heading that truncates - including ones written later - and it only
 * appears when the text really is cut, so a label that fits shows nothing.
 * It also appears only over the dots, not the whole label, so it never gets
 * in the way of reading or clicking what is there.
 *
 * Plain DOM, not React state: moving the pointer must not re-render the page.
 */
import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";

/** How far up from the hovered node to look for the element that truncates. */
const MAX_DEPTH = 4;

/** Whether the pointer is on the trailing "…" of a truncated element. */
export function overEllipsis(el, clientX, clientY, view = typeof window !== "undefined" ? window : null) {
  const rect = el.getBoundingClientRect();
  if (!rect || rect.width <= 0) return false;
  const fontSize = parseFloat(view?.getComputedStyle?.(el)?.fontSize) || 14;
  const zone = Math.max(14, fontSize * 1.2);
  const rtl = view?.getComputedStyle?.(el)?.direction === "rtl";
  const inX = rtl ? clientX >= rect.left && clientX <= rect.left + zone : clientX >= rect.right - zone && clientX <= rect.right;
  return inX && clientY >= rect.top - 2 && clientY <= rect.bottom + 2;
}

/** The element that cuts its own text with an ellipsis, if the hover is on one. */
export function truncatedElement(node, view = typeof window !== "undefined" ? window : null) {
  let el = node;
  for (let depth = 0; el && el.nodeType === 1 && depth < MAX_DEPTH; depth += 1, el = el.parentElement) {
    if (el.closest?.("[data-truncation-tip]") || el.hasAttribute?.("title")) return null;
    const style = view?.getComputedStyle ? view.getComputedStyle(el) : null;
    if (style && style.textOverflow === "ellipsis" && el.scrollWidth > el.clientWidth + 1) {
      return (el.textContent || "").trim() ? el : null;
    }
  }
  // An outlined field whose label sits inside the empty box: the label lets
  // the pointer through to the input, so the input stands in for it.
  const base = node?.closest?.(".MuiInputBase-root");
  const label = base?.parentElement?.querySelector?.(":scope > .MuiInputLabel-root, :scope > .MuiFormLabel-root");
  if (label && label.scrollWidth > label.clientWidth + 1 && (label.textContent || "").trim()) return label;
  return null;
}

export default function TruncatedTextTooltip() {
  const theme = useTheme();
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const tip = document.createElement("div");
    tip.setAttribute("data-truncation-tip", "");
    tip.setAttribute("role", "tooltip");
    Object.assign(tip.style, {
      position: "fixed",
      zIndex: "2000",
      display: "none",
      maxWidth: "360px",
      padding: "4px 8px",
      borderRadius: "4px",
      // White, black letters, in both colour modes: a note to read, not a chip.
      background: "#fff",
      color: "#111",
      border: "1px solid rgba(0,0,0,0.18)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
      font: `500 12.5px/1.4 ${theme.typography.fontFamily}`,
      pointerEvents: "none",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    });
    document.body.appendChild(tip);
    let current = null;

    const hide = () => {
      current = null;
      tip.style.display = "none";
    };
    const show = (el) => {
      current = el;
      tip.textContent = (el.textContent || "").replace(/\s+/g, " ").trim();
      tip.style.display = "block";
      const box = el.getBoundingClientRect();
      const width = tip.offsetWidth;
      const left = Math.max(8, Math.min(box.left, window.innerWidth - width - 8));
      const above = box.top - tip.offsetHeight - 6;
      tip.style.left = `${left}px`;
      tip.style.top = `${above >= 4 ? above : box.bottom + 6}px`;
    };
    // The truncated element under the pointer is looked up when the target
    // changes; every move only checks whether the pointer is on its dots.
    let lastTarget = null, candidate = null;
    const onMove = (event) => {
      if (event.target !== lastTarget) {
        lastTarget = event.target;
        candidate = truncatedElement(event.target);
      }
      const el = candidate && overEllipsis(candidate, event.clientX, event.clientY) ? candidate : null;
      if (el === current) return;
      if (el) show(el);
      else hide();
    };
    const onLeave = () => { lastTarget = null; candidate = null; hide(); };
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("mouseleave", onLeave, true);
    document.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("mouseleave", onLeave, true);
      document.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
      tip.remove();
    };
  }, [theme.palette.mode, theme.typography.fontFamily]);
  return null;
}
